// 單路回放的來源預設、記憶與逾時回退（VMS UX 評審第五輪 R5-P0-1、B5-02 / B5-03；SPEC #vms-web-459「單路的來源預設與回退」）。
//   - 預設依序：使用者這次選的（網址 src=，換台也帶著）→ 這台最近成功播過的 → 從事件來時有片段的雲端 → 合併時間軸的預設
//   - 本地來源（NVR 硬碟 / SD 卡）讀不到或逾時沒有畫面：記在這台電腦 30 分鐘（以錄影主機為單位），這段時間預設不選它
//   - 記憶存在瀏覽器（localStorage，跟工作站走、不跟帳號走）；存不了就只在這次有效
import type { RecordingSource, TimelineSpan } from '@kinio/web-sdk';

export const LOCAL_SOURCES: RecordingSource[] = ['nvr', 'sd', 'phone'];

/** 開始播多久還沒有畫面就自動改用有片段的其他來源（第五輪：不要等 15 秒） */
export const PLAY_FALLBACK_MS = 8_000;

/** 本地來源讀不到的記憶多久（秒） */
export const BROKEN_TTL_SEC = 30 * 60;

const KEY = 'kinio.vms.pbSource.v1';
const MAX_ENTRIES = 200;

interface Mark {
  src: RecordingSource;
  /** unix 秒 */
  at: number;
}

export interface SourceMemory {
  /** 攝影機 key → 最近成功播出畫面的來源 */
  ok: Record<string, Mark>;
  /** 錄影主機 key（<home>/<本地 AID>）→ 最近讀不到的本地來源 */
  broken: Record<string, Mark>;
}

export function parseMemory(raw: string | null): SourceMemory {
  try {
    const v = JSON.parse(raw ?? '') as Partial<SourceMemory>;
    return { ok: v && typeof v.ok === 'object' && v.ok ? v.ok : {}, broken: v && typeof v.broken === 'object' && v.broken ? v.broken : {} };
  } catch {
    return { ok: {}, broken: {} };
  }
}

let mem: SourceMemory | null = null;

function load(): SourceMemory {
  if (mem) return mem;
  try {
    mem = parseMemory(localStorage.getItem(KEY));
  } catch {
    mem = { ok: {}, broken: {} };
  }
  return mem;
}

function trim(r: Record<string, Mark>): Record<string, Mark> {
  const e = Object.entries(r);
  if (e.length <= MAX_ENTRIES) return r;
  return Object.fromEntries(e.sort((a, b) => b[1].at - a[1].at).slice(0, MAX_ENTRIES));
}

function save(m: SourceMemory) {
  mem = { ok: trim(m.ok), broken: trim(m.broken) };
  try {
    localStorage.setItem(KEY, JSON.stringify(mem));
  } catch {
    /* 存不了：這次有效就好 */
  }
}

/** 測試用：清掉記憶體裡的快取。 */
export function resetSourceMemory() {
  mem = null;
}

/** 這台播出畫面了：記住來源；本地來源播得出來就把這台錄影主機的「讀不到」清掉。 */
export function rememberOk(cam: string, host: string, src: RecordingSource, now = Date.now() / 1000) {
  const m = load();
  const ok = { ...m.ok, [cam]: { src, at: now } };
  const broken = { ...m.broken };
  if (LOCAL_SOURCES.includes(src) && broken[host]?.src === src) delete broken[host];
  if (m.ok[cam]?.src === src && broken === m.broken) return;
  save({ ok, broken });
}

/** 這台錄影主機的本地來源讀不到（隧道開不起來、HLS 致命錯誤、逾時沒有畫面）。 */
export function rememberBroken(host: string, src: RecordingSource, now = Date.now() / 1000) {
  if (!LOCAL_SOURCES.includes(src)) return;
  const m = load();
  save({ ok: m.ok, broken: { ...m.broken, [host]: { src, at: now } } });
}

export function forgetBroken(host: string) {
  const m = load();
  if (!m.broken[host]) return;
  const broken = { ...m.broken };
  delete broken[host];
  save({ ok: m.ok, broken });
}

export function lastOkSource(cam: string): RecordingSource | null {
  return load().ok[cam]?.src ?? null;
}

/** 這些攝影機裡，這台電腦最近成功播過的那一台（回放頁沒指定攝影機時的預設，第六輪 B6-14）；都沒播過 = null。 */
export function lastPlayedCamera(keys: string[]): string | null {
  const ok = load().ok;
  let best: string | null = null;
  for (const k of keys) if (ok[k] && (!best || ok[k].at > ok[best].at)) best = k;
  return best;
}

/** 30 分鐘內讀不到過的本地來源（沒有 = null）。 */
export function brokenSource(host: string, now = Date.now() / 1000): RecordingSource | null {
  const b = load().broken[host];
  return b && now - b.at < BROKEN_TTL_SEC ? b.src : null;
}

export interface CoverageLike {
  source: RecordingSource;
  spans: TimelineSpan[];
}

function covers(cov: CoverageLike[], src: RecordingSource, at: number): boolean {
  return cov.some((c) => c.source === src && c.spans.some((s) => at >= s.start && at < s.end));
}

function hasAfter(cov: CoverageLike[], src: RecordingSource, at: number): boolean {
  return cov.some((c) => c.source === src && c.spans.some((s) => s.end > at));
}

export type PickReason = 'last_ok' | 'from_event' | 'avoid_broken';

/**
 * 沒有指定來源時，這一刻要不要改用合併時間軸預設以外的來源。回 null = 照合併時間軸的預設。
 * defaultSrc = 合併時間軸在這一刻（或之後最近）選到的來源。
 */
export function preferredSource(
  cov: CoverageLike[],
  at: number,
  o: { defaultSrc: RecordingSource | null; lastOk: RecordingSource | null; broken: RecordingSource | null; fromEvent: boolean },
): { src: RecordingSource; why: PickReason } | null {
  const usable = (s: RecordingSource) => s !== o.broken;
  // ① 這台最近成功播過的來源，這一刻有片段 → 用它
  if (o.lastOk && o.lastOk !== o.defaultSrc && usable(o.lastOk) && covers(cov, o.lastOk, at)) return { src: o.lastOk, why: 'last_ok' };
  if (o.lastOk && o.lastOk === o.defaultSrc && usable(o.lastOk)) return null;
  // ② 從事件來：事件那一刻有雲端片段 → 先播雲端（事件片段一定是真的片段；本地硬碟不一定讀得到）
  if (o.fromEvent && o.defaultSrc !== 'cloud' && covers(cov, 'cloud', at) && !(o.lastOk && o.lastOk === o.defaultSrc)) return { src: 'cloud', why: 'from_event' };
  // ③ 預設的本地來源最近讀不到 → 改用這一刻（或之後最近）有片段的其他來源
  if (o.defaultSrc && o.broken === o.defaultSrc) {
    const others = cov.map((c) => c.source).filter((s, i, a) => a.indexOf(s) === i && s !== o.broken && !(LOCAL_SOURCES.includes(s) && LOCAL_SOURCES.includes(o.broken!)));
    const now = others.find((s) => covers(cov, s, at));
    if (now) return { src: now, why: 'avoid_broken' };
    const later = others.find((s) => hasAfter(cov, s, at));
    if (later) return { src: later, why: 'avoid_broken' };
  }
  return null;
}

/** 網址 src= 的值（cloud / local / backup）；node 不能指定 → null。 */
export function srcParamOf(src: RecordingSource): 'cloud' | 'local' | 'backup' | null {
  if (src === 'cloud' || src === 'backup') return src;
  return LOCAL_SOURCES.includes(src) ? 'local' : null;
}

/**
 * 逾時沒有畫面時要改用哪個來源：這一刻（或之後最近）有片段、還沒試過、不是同一類本地來源的；沒有 = null（只顯示「載入很久」）。
 */
export function timeoutFallback(cov: CoverageLike[], failed: RecordingSource, tried: RecordingSource[], at: number): RecordingSource | null {
  const ok = (s: RecordingSource) => s !== failed && !tried.includes(s) && !(LOCAL_SOURCES.includes(s) && LOCAL_SOURCES.includes(failed));
  const srcs = cov.map((c) => c.source).filter((s, i, a) => a.indexOf(s) === i && ok(s));
  return srcs.find((s) => covers(cov, s, at)) ?? srcs.find((s) => hasAfter(cov, s, at)) ?? null;
}
